import { Box, CircularProgress, Typography } from "@mui/material";
import { Colors } from "Components/Layout/Themes/Colors";
import { UserIcon } from "Components/Shared/Icons";
import { useImageObjectUrl } from "Hooks/useImageObjectUrl";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import styled, { type DefaultTheme } from "styled-components";

type Props = {
  file: File | null;
  title: string;
  id?: string;
  description?: string;
  onChange: (file: File) => void;
  acceptFileName?: string;
  isLoading?: boolean;
  isError?: boolean;
};

const getBorderColor = (
  theme: DefaultTheme,
  isDragActive: boolean,
  isError?: boolean,
) => {
  if (isError) {
    return theme.colors.red;
  }

  return isDragActive ? theme.colors.darkGray : theme.colors.border;
};

const StyledWrapper = styled.div<{
  $isDragActive: boolean;
  $isError?: boolean;
}>`
  padding: ${(props) => props.theme.spacing(2, 3)};
  border: 1px dashed
    ${(props) => getBorderColor(props.theme, props.$isDragActive, props.$isError)};

  .label {
    font-weight: 700;
  }

  .file-names {
    font-weight: 300;
    font-size: 12px;
  }

  svg {
    width: 35px;
    height: auto;
    stroke-width: 20px;

    path {
      stroke-width: 1px;
    }
    #cloud {
      fill: #fff;
    }
  }
`;

export const DragNDrop = ({
  onChange,
  title,
  description,
  id,
  isError,
  file: propsFile,
  isLoading,
}: Props) => {
  const [file, setFile] = useState<File | null>(propsFile);
  const previewUrl = useImageObjectUrl(file);

  const acceptFiles = {
    "image/png": [".png"],
    "image/jpeg": [".jpeg", ".jpg"],
    "application/pdf": [".pdf"],
  };

  const onDrop = useCallback(
    (acceptedFiles: Array<File>) => {
      setFile(acceptedFiles[0]);
      onChange(acceptedFiles[0]);
    },
    [onChange],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: acceptFiles,
  });

  const renderIcon = () => {
    if (isLoading) {
      return <CircularProgress size={21} color="secondary" />;
    }
    if (previewUrl) {
      return (
        <Box
          component="img"
          src={previewUrl}
          alt={file?.name}
          sx={{ width: 84, height: 56, objectFit: "cover", borderRadius: 1 }}
        />
      );
    }
    return <UserIcon color={Colors.SecondaryText} height="36" width="36" />;
  };

  return (
    <StyledWrapper
      {...getRootProps()}
      $isDragActive={isDragActive}
      $isError={isError}
    >
      <input {...getInputProps({ id })} />
      <Box display="flex" alignItems="center">
        <Box marginRight={4} lineHeight={0} flexShrink={0}>
          {renderIcon()}
        </Box>
        <Box display="grid" alignItems="center" width="100%" minWidth={0}>
          {/* Empty-state copy stays mounted (hidden) to reserve its height,
              so selecting a file doesn't shrink the box. */}
          <Box gridArea="1 / 1" visibility={file ? "hidden" : "visible"}>
            <Typography fontWeight={500} fontSize={18} textAlign="center">
              {title}
            </Typography>
            {!!description && (
              <Typography textAlign="center">{description}</Typography>
            )}
          </Box>
          {file && (
            <Typography
              gridArea="1 / 1"
              minWidth={0}
              noWrap
              fontWeight={600}
              textAlign="center"
            >
              {file.name}
            </Typography>
          )}
        </Box>
      </Box>
    </StyledWrapper>
  );
};
